import React, { useEffect, useRef, useState } from 'react';
import { formatAIResponse } from '../../utils/formatAIResponse';
import { agentFetch } from '../../utils/agentFetch';
import client from '../../api/client';

const RDF_AVATAR = '/RDF_Logo.svg';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  model?: string;
  spendWarning?: string;
  isStreaming?: boolean;
  /** User's own wording when they picked a refined question; content holds the refined one. */
  originalQuestion?: string;
  /** Folders added to the search because a refined question matched better there. */
  alsoSearched?: string[];
}

interface Props {
  messages: Message[];
  streamText: string;
  isStreaming: boolean;
  onSuggestionClick: (text: string) => void;
  onNewConversation: () => void;
}

function CopyButton({ content }: { content: string }) {
  const [label, setLabel] = useState('Copy');
  return (
    <button
      className="ai-btn-copy"
      onClick={() => {
        const text = content.replace(/<[^>]+>/g, '');
        navigator.clipboard.writeText(text).then(() => {
          setLabel('Copied!');
          setTimeout(() => setLabel('Copy'), 1800);
        }).catch(() => {
          setLabel('Failed');
          setTimeout(() => setLabel('Copy'), 1800);
        });
      }}
    >
      {label}
    </button>
  );
}

function FeedbackButtons({ query, model }: { query: string; model: string }) {
  const [submitted, setSubmitted] = useState<'positive' | 'negative' | null>(null);

  const submit = async (rating: 'positive' | 'negative') => {
    if (submitted) return;
    setSubmitted(rating);
    try {
      await agentFetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, query, model }),
      });
    } catch (_) {}
  };

  return (
    <div className="ai-feedback-btns">
      <button
        className={`ai-feedback-btn${submitted === 'positive' ? ' selected' : ''}`}
        disabled={!!submitted}
        onClick={() => submit('positive')}
        title="Good response"
      >
        👍
      </button>
      <button
        className={`ai-feedback-btn${submitted === 'negative' ? ' selected' : ''}`}
        disabled={!!submitted}
        onClick={() => submit('negative')}
        title="Poor response"
      >
        👎
      </button>
    </div>
  );
}

function FollowupChips({
  query,
  responseText,
  onChipClick,
}: {
  query: string;
  responseText: string;
  onChipClick: (text: string) => void;
}) {
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    agentFetch('/api/suggest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, response: responseText.slice(0, 1000) }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data?.suggestions?.length) {
          setSuggestions(data.suggestions);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [query, responseText]);

  if (!suggestions.length) return null;
  return (
    <div className="ai-followup-chips">
      {suggestions.map((s, i) => (
        <button key={i} className="ai-followup-chip" onClick={() => onChipClick(s)}>
          {s}
        </button>
      ))}
    </div>
  );
}

export const ChatMessages: React.FC<Props> = ({ messages, streamText, isStreaming, onSuggestionClick, onNewConversation }) => {
  const historyRef = useRef<HTMLDivElement>(null);
  const wasStreamingRef = useRef(false);

  useEffect(() => {
    const el = historyRef.current;
    if (!el) return;
    if (!isStreaming && wasStreamingRef.current) {
      // Streaming just finished:
      // 1. Scroll the entire page to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // 2. Scroll the chat container so the last assistant message is at the top
      //    Using scrollTop instead of scrollIntoView to avoid it moving the window
      const msgs = el.querySelectorAll('.ai-message-assistant');
      const last = msgs[msgs.length - 1] as HTMLElement | undefined;
      if (last) {
        setTimeout(() => {
          el.scrollTop = last.offsetTop - el.offsetTop;
        }, 150);
      }
    } else {
      // During streaming or new messages — scroll to bottom
      el.scrollTop = el.scrollHeight;
    }
    wasStreamingRef.current = isStreaming;
  }, [messages, streamText, isStreaming]);

  // Intercept citation "View Source" clicks — fetch with auth, open as blob.
  // window.open must be called synchronously (before the async fetch) to avoid
  // mobile popup blockers. We open a blank tab immediately, then navigate it.
  useEffect(() => {
    const el = historyRef.current;
    if (!el) return;
    const handleClick = async (e: MouseEvent) => {
      const link = (e.target as Element).closest('a.ai-ref-link');
      if (!link) return;
      e.preventDefault();
      const href = (link as HTMLAnchorElement).href;
      const match = href.match(/\/api\/source\/([a-zA-Z0-9_-]+)/);
      if (!match) { window.open(href, '_blank'); return; }
      // Open blank tab synchronously so popup blockers don't interfere
      const newTab = window.open('', '_blank');
      try {
        const res = await client.get(`/source/${match[1]}`, { responseType: 'blob' });
        const blob = new Blob([res.data], { type: res.data.type });
        const url = URL.createObjectURL(blob);
        if (newTab) {
          newTab.location.href = url;
        } else {
          window.location.href = url;
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch {
        if (newTab) newTab.close();
        window.location.href = href;
      }
    };
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, []);

  return (
    <div className="ai-chat-history" ref={historyRef}>
      {/* Welcome message */}
      {messages.length === 0 && !isStreaming && (
        <div className="ai-message ai-message-assistant">
          <div className="ai-avatar">
            <img src={RDF_AVATAR} alt="RDF" />
          </div>
          <div className="ai-message-content">
            <p>Hi! Ask me a question. I search knowledge bases you select, but always verify my facts. The first request takes a bit longer whilst I warm up!</p>
          </div>
        </div>
      )}

      {messages.map((msg, idx) => (
        <div key={msg.id} className={`ai-message ai-message-${msg.role}`}>
          <div className="ai-avatar">
            {msg.role === 'user'
              ? <span className="ai-avatar-user">U</span>
              : <img src={RDF_AVATAR} alt="RDF" />}
          </div>
          <div className="ai-message-content">
            {msg.role === 'user' ? (
              <>
                <p>{msg.originalQuestion || msg.content}</p>
                {msg.originalQuestion && (
                  <p className="ai-refined-note">Refined to: {msg.content}</p>
                )}
                {msg.alsoSearched && msg.alsoSearched.length > 0 && (
                  <p className="ai-refined-note">Also searched: {msg.alsoSearched.join(', ')}</p>
                )}
              </>
            ) : (
              <>
                <div dangerouslySetInnerHTML={{ __html: formatAIResponse(msg.content) }} />
                {msg.spendWarning && (
                  <div className="ai-spend-warning">⚠ {msg.spendWarning}</div>
                )}
                <div className="ai-message-actions">
                  <CopyButton content={msg.content} />
                  <FeedbackButtons
                    query={messages[idx - 1]?.content || ''}
                    model={msg.model || 'claude-sonnet-4-6'}
                  />
                  {idx === messages.length - 1 && (
                    <button className="ai-btn-toolbar" onClick={onNewConversation} title="New conversation">
                      ↺ New
                    </button>
                  )}
                </div>
                <FollowupChips
                  query={messages[idx - 1]?.content || ''}
                  responseText={msg.content}
                  onChipClick={onSuggestionClick}
                />
              </>
            )}
          </div>
        </div>
      ))}

      {/* Streaming bubble */}
      {isStreaming && (
        <div className="ai-message ai-message-assistant">
          <div className="ai-avatar">
            <img src={RDF_AVATAR} alt="RDF" />
          </div>
          <div className="ai-message-content">
            {streamText
              ? <span>{streamText}▊</span>
              : <span className="ai-thinking"><i>Running AI reasoning…</i></span>}
          </div>
        </div>
      )}

    </div>
  );
};
